import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Trash2, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { products } from '../data/mockData';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { favorites, removeFavorite, isLoading: favLoading } = useFavorites();
  const { addToCart, setIsCartOpen } = useCart();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || favLoading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const favoriteProducts = products.filter((p) => favorites.includes(p.id));

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = (product) => {
    const cartProduct = {
      ...product,
      selectedSize: product.sizes?.[0] || '65x65',
      selectedColor: product.colors?.[0],
      price: product.prices?.['65x65'] || product.price,
    };
    addToCart(cartProduct, cartProduct.selectedColor, 1);
    setIsCartOpen(true);
  };

  return (
    <div className="pt-32 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-light tracking-wider mb-4">FAVORİLERİM</h1>
          <p className="text-gray-500">
            {favoriteProducts.length > 0
              ? `${favoriteProducts.length} ürün favori listenizde`
              : 'Henüz favori ürününüz yok'}
          </p>
        </div>

        {/* Empty State */}
        {favoriteProducts.length === 0 ? (
          <div className="text-center py-16">
            <Heart size={64} className="mx-auto text-gray-300 mb-6" />
            <h2 className="text-xl font-light mb-4">Favori listeniz boş</h2>
            <p className="text-gray-500 mb-8">
              Beğendiğiniz ürünleri favorilere ekleyerek daha sonra kolayca ulaşabilirsiniz.
            </p>
            <Link
              to="/urunler"
              className="inline-block bg-black text-white px-8 py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors"
            >
              ÜRÜNLERİ KEŞFET
            </Link>
          </div>
        ) : (
          /* Favorites Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {favoriteProducts.map((product) => (
              <div key={product.id} className="group relative">
                {/* Remove Button */}
                <button
                  onClick={() => removeFavorite(product.id)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50 hover:text-red-500 transition-colors"
                  title="Favorilerden kaldır"
                >
                  <Trash2 size={18} />
                </button>

                {/* Product Image */}
                <Link to={`/urun/${product.id}`}>
                  <div className="relative aspect-square overflow-hidden bg-gray-100 mb-4">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x400?text=VALMÉRA';
                      }}
                    />
                    {product.isNew && (
                      <span className="absolute top-4 left-4 bg-black text-white text-xs px-3 py-1 tracking-wider">
                        YENİ
                      </span>
                    )}
                  </div>
                </Link>

                {/* Product Info */}
                <div className="text-center">
                  <Link to={`/urun/${product.id}`}>
                    <h3 className="text-sm font-medium mb-2 group-hover:underline">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-sm mb-4">
                    {formatPrice(product.prices?.['65x65'] || product.price)}
                  </p>
                  <button
                    data-testid={`favorites-buy-shopier-${product.id}`}
                    onClick={() => window.open('https://www.shopier.com/valmeraofficial', '_blank', 'noopener,noreferrer')}
                    className="w-full flex items-center justify-center gap-2 bg-black text-white py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
                  >
                    <ShoppingBag size={16} />
                    SATIN AL
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
